import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AdminShell } from "@/components/AdminShell";
import { PlanCostRows, PlanCostBreakdown } from "@/components/admin/PlanCostBreakdown";
import { PlanComparisonMatrix } from "@/components/admin/PlanComparisonMatrix";
import { Slider } from "@/components/ui/slider";
import { formatSek, EGRESS_SEK_PER_GB, STORAGE_SEK_PER_GB_MONTH, WEB_SEARCH_SEK } from "@/lib/ai-costs";
import {
  COST_ASSUMPTIONS,
  breakEvenUsage,
  marginFor,
  planCost,
  DAYS_PER_MONTH,
} from "@/lib/plan-costs";
import { PLANS, PLAN_ORDER } from "@/lib/plans";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/plankostnader")({
  head: () => ({
    meta: [
      { title: "Kostnad per abonnemang — Administration" },
      {
        name: "description",
        content:
          "Intern kalkyl: vad varje abonnemang kan kosta oss per månad om användaren utnyttjar alla gränser.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PlanCostsPage,
});

function mb(bytes: number) {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function PlanCostsPage() {
  const [usagePct, setUsagePct] = useState(20);
  const usage = usagePct / 100;
  const costs = PLAN_ORDER.map((id) => ({ id, cost: planCost(id) }));

  return (
    <AdminShell title="Kostnad per abonnemang">
      <div className="space-y-10">
        <section>
          <h2 className="font-heading text-lg font-bold text-foreground">Översikt</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Taket är vad ett konto kostar oss om varje gräns i planen används fullt ut varje månad.
            Dra i reglaget för att se en mer realistisk användningsgrad.
          </p>

          <div className="mt-4 max-w-md">
            <label className="text-xs font-semibold text-foreground">
              Användningsgrad: {usagePct} %
            </label>
            <Slider
              className="mt-2"
              value={[usagePct]}
              min={5}
              max={100}
              step={5}
              onValueChange={(v) => setUsagePct(v[0] ?? 20)}
            />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {costs.map(({ id, cost }) => {
              const m = marginFor(cost, usage);
              const be = breakEvenUsage(cost);
              const perAd = cost.adsPerMonth > 0 ? cost.maxSek / cost.adsPerMonth : 0;
              return (
                <div
                  key={id}
                  className="rounded-xl border border-border bg-card p-4 shadow-sm"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-heading text-base font-bold text-foreground">
                      {PLANS[id].name}
                    </h3>
                    <span className="text-xs text-muted-foreground">{PLANS[id].priceLabel}</span>
                  </div>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <Stat label="Annonser/mån" value={String(cost.adsPerMonth)} />
                    <Stat label="Tak" value={`${formatSek(cost.maxSek)}/mån`} />
                    <Stat label={`Vid ${usagePct} %`} value={`${formatSek(m.spend)}/mån`} />
                    <Stat label="Kostnad per annons" value={formatSek(perAd, 2)} />
                    <Stat
                      label={`Marginal vid ${usagePct} %`}
                      value={formatSek(m.marginSek)}
                      tone={m.marginSek < 0 ? "bad" : "good"}
                    />
                  </dl>
                  <p className="mt-3 border-t border-border/60 pt-2 text-[11px] leading-snug text-muted-foreground">
                    {be === null
                      ? "Gratisplanen har ingen intäkt – hela kostnaden är förvärvskostnad."
                      : `Går back när mer än ${Math.round(be * 100)} % av gränserna används.`}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-lg font-bold text-foreground">
            Alla planer sida vid sida
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Antal per månad, styckpris och summa för varje funktion. Den blå pilen visar hur mycket
            mer man får jämfört med planen till vänster. Streck betyder att funktionen inte ingår.
          </p>
          <div className="mt-4">
            <PlanComparisonMatrix usage={usage} />
          </div>
        </section>


        <section className="space-y-6">
          <h2 className="font-heading text-lg font-bold text-foreground">Rad för rad</h2>
          {costs.map(({ id, cost }) => (
            <div key={id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-heading text-base font-bold text-foreground">
                  {PLANS[id].name}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {formatSek(PLANS[id].priceSek)}/mån · tak {formatSek(cost.maxSek)}/mån
                </p>
              </div>
              <div className="mt-3">
                <PlanCostRows cost={cost} />
              </div>
            </div>
          ))}
        </section>

        <section>
          <h2 className="font-heading text-lg font-bold text-foreground">Priser vi räknar med</h2>
          <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
            <li>AI-anrop prissätts per token med de uppmätta snitten på sidan AI-kostnader.</li>
            <li>Marknadssökning hos Perplexity: {formatSek(WEB_SEARCH_SEK)} per sökning.</li>
            <li>Lagring: {formatSek(STORAGE_SEK_PER_GB_MONTH)} per GB och månad.</li>
            <li>Datatrafik ut: {formatSek(EGRESS_SEK_PER_GB)} per GB.</li>
            <li>En månad räknas som {DAYS_PER_MONTH} dygn.</li>
          </ul>

          <h2 className="mt-6 font-heading text-lg font-bold text-foreground">Antaganden</h2>
          <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
            <li>Annonsbild efter komprimering: {mb(COST_ASSUMPTIONS.imageBytes * 1)} (~700 kB).</li>
            <li>
              Video på annons: {mb(COST_ASSUMPTIONS.adVideoBytes)} och läggs på{" "}
              {Math.round(COST_ASSUMPTIONS.adVideoShare * 100)} % av annonserna.
            </li>
            <li>Material ligger kvar {COST_ASSUMPTIONS.storageMonths} månader.</li>
            <li>
              {COST_ASSUMPTIONS.viewsPerAd} listvisningar per annons med miniatyr (
              {Math.round(COST_ASSUMPTIONS.thumbBytes / 1024)} kB), varav{" "}
              {Math.round(COST_ASSUMPTIONS.detailViewShare * 100)} % öppnas och hämtar{" "}
              {COST_ASSUMPTIONS.imagesPerDetailView} stora bilder.
            </li>
            <li>
              Djupvärdering: {COST_ASSUMPTIONS.marketSearchesPerValuation} marknadssökningar,{" "}
              {COST_ASSUMPTIONS.lensSearchesPerValuation} omvända bildsökningar och{" "}
              {COST_ASSUMPTIONS.visionCallsPerValuation} Vision-anrop per värdering.
            </li>
            <li>
              Videoskanning: {mb(COST_ASSUMPTIONS.scanVideoBytes)} råfilm,{" "}
              {COST_ASSUMPTIONS.scanFramesPerRun} sparade bildrutor och{" "}
              {COST_ASSUMPTIONS.videoPartsPerScan} delanrop per körning.
            </li>
            <li>
              Där planen saknar tak räknar vi med {COST_ASSUMPTIONS.unlimitedAdsPerMonth} annonser,{" "}
              {COST_ASSUMPTIONS.unlimitedRewritesPerMonth} omskrivningar och{" "}
              {COST_ASSUMPTIONS.unlimitedDeepValuationsPerMonth} djupvärderingar per månad.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-bold text-foreground">Så ser det ut på priser</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Samma ruta visas under varje plankort på prissidan när du är inloggad som admin.
          </p>
          <div className="mt-3 max-w-md">
            <PlanCostBreakdown planId="pro" usage={usage} withLink={false} defaultOpen />
          </div>
        </section>
      </div>
    </AdminShell>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "font-semibold tabular-nums",
          tone === "bad"
            ? "text-destructive"
            : tone === "good"
              ? "text-green-600"
              : "text-foreground",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
